import { Controller } from 'react-hook-form';
import { css } from '@bts-web/utils-style-engine';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { AssetImage, ButtonBase, CheckboxToggle } from '../../../../common';
import { SelectWalletHostButton } from './SelectWalletHostButton';
import { OtherWalletId } from '../SelectWalletHostScreen/SelectWalletHostScreen';
import { type DestinationTagValues } from '../../../types';
import { type CryptoTransfersVASPNode } from '../../TransfersLoader/types';
import {
  useWalletFormInput,
  type WalletFormInputsProps,
} from './useWalletFormInput';
import { RegularDismissCircle } from '../../../../common/svgIcons';

interface WalletFormProps
  extends React.PropsWithChildren,
    WalletFormInputsProps {
  title: string;
  submitButtonLabel: string;
  recipientNameLabel: string;
  actionLabel: string;
  isWalletAddressDisabled?: boolean;
  isLegalEntity?: boolean;
}

const inputSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'extra_small_2',
});

const inputStyles = css({
  width: '100%',
  padding: 'small',
  border: '1px solid',
  borderColor: 'neutrals.fill_tertiary',
  borderRadius: '4px',
  marginTop: 'extra_small_4',
  fontSize: 'label.large',
  fontWeight: 'label.large',
  lineHeight: 'label.large',
  letterSpacing: 'label.large',
  backgroundColor: 'white',
  '&:disabled': {
    backgroundColor: 'neutrals.fill_disabled',
  },
  '&[aria-invalid="true"]': {
    borderColor: 'negative.stroke_primary',
  },
});

const inputTitleErrorStyles = css({
  color: 'negative.text_primary',
});

const inputTitleStyles = css({
  color: 'neutrals.text_primary',
  fontSize: 'label.medium_medium',
  fontWeight: 'label.medium_medium',
  lineHeight: 'label.medium_medium',
  letterSpacing: 'label.medium_medium',
});

const inputErrorContainerStyles = css({
  color: 'negative.text_primary',
  display: 'flex',
  gap: 'extra_small_3',
});

const inputErrorTextStyles = css({
  fontSize: 'caption.small_light',
  fontWeight: 'caption.small_light',
  lineHeight: 'caption.small_light',
  letterSpacing: 'caption.small_light',
});

const WalletForm = ({
  translations,
  assetData,
  networkData,
  walletHosts,
  onCreate,
  onUpdate,
  title,
  submitButtonLabel,
  recipientNameLabel,
  actionLabel,
  defaultAddressInput,
  isWalletAddressDisabled,
  isLegalEntity = false,
  children,
}: WalletFormProps) => {
  const { t } = useClientTranslation();

  const {
    control,
    errors,
    isMyWallet,
    walletHost,
    defaultWalletHost,
    walletAddressInputProps,
    isMyWalletInputProps,
    recipientNameInputProps,
    leiNumberInputProps,
    memoInputProps,
    otherHostNameInputProps,
    walletHostInputProps,
    isSubmitDisabled,
    isSubmitting,
    onSubmitWalletForm,
  } = useWalletFormInput({
    translations,
    assetData,
    networkData,
    walletHosts,
    onCreate,
    onUpdate,
    defaultAddressInput,
    isLegalEntity,
  });

  return (
    <form
      data-testid="wallet-form"
      className={css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
      })}
      onSubmit={onSubmitWalletForm}
    >
      <h1
        className={css({
          fontSize: 'headline.large_semi_bold',
          fontWeight: 'headline.large_semi_bold',
          lineHeight: 'headline.large_semi_bold',
          letterSpacing: 'headline.large_semi_bold',
          paddingY: 'small',
        })}
      >
        {title}
      </h1>
      <div
        className={css({
          display: 'flex',
          gap: 'small',
          alignItems: 'center',
          paddingY: 'small',
          borderBottomWidth: '1px',
          borderBottomColor: 'neutrals.divider',
        })}
      >
        <AssetImage
          src={assetData?.logoUrl}
          alt={assetData?.name as string}
          width={40}
          height={40}
        />
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small_2',
          })}
        >
          <span
            className={css({
              color: 'neutrals.text_primary',
              fontSize: 'title.large',
              fontWeight: 'title.large',
              lineHeight: 'title.large',
              letterSpacing: 'title.large',
            })}
          >
            {actionLabel} {assetData?.name}
          </span>
          <span
            className={css({
              color: 'neutrals.text_secondary',
              fontSize: 'body.medium',
              fontWeight: 'body.medium',
              lineHeight: 'body.medium',
              letterSpacing: 'body.medium',
            })}
          >
            {t('on_network', {
              assetname: networkData?.name,
            })}
          </span>
        </div>
      </div>

      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'medium',
          py: 'small',
        })}
      >
        {(!isMyWallet || isLegalEntity) && (
          <div className={inputSectionStyles}>
            <span
              className={`${inputTitleStyles} ${errors.recipientName ? inputTitleErrorStyles : ''}`}
            >
              {recipientNameLabel}
            </span>
            <input
              aria-invalid={errors.recipientName ? 'true' : 'false'}
              className={inputStyles}
              placeholder={
                isLegalEntity
                  ? translations.registeredBusinessNamePlaceholder
                  : translations.firstAndLastName
              }
              id="recipientNameInput"
              {...recipientNameInputProps}
              {...(!isLegalEntity && {
                onInput: (e) => {
                  // Remove any digits (0-9) from the input
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /\d/g,
                    '',
                  );
                },
                pattern: '[^\\d]+',
              })}
            />
            {errors.recipientName && (
              <div className={inputErrorContainerStyles}>
                <RegularDismissCircle size={12} color="negative.text_primary" />
                <span className={inputErrorTextStyles}>
                  {errors.recipientName.message as string}
                </span>
              </div>
            )}
          </div>
        )}

        {isLegalEntity && (
          <div className={inputSectionStyles}>
            <span
              className={`${inputTitleStyles} ${errors.leiNumber ? inputTitleErrorStyles : ''}`}
            >
              {translations.leiNumber}
            </span>
            <input
              aria-invalid={errors.leiNumber ? 'true' : 'false'}
              className={inputStyles}
              placeholder={translations.leiNumberPlaceholder}
              id="leiNumberInput"
              {...leiNumberInputProps}
            />
            {errors.leiNumber && (
              <div className={inputErrorContainerStyles}>
                <RegularDismissCircle size={12} color="negative.text_primary" />
                <span className={inputErrorTextStyles}>
                  {errors.leiNumber.message as string}
                </span>
              </div>
            )}
          </div>
        )}

        {!isLegalEntity && (
          <div className={inputSectionStyles}>
            <div
              className={css({
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              })}
            >
              <span
                className={css({
                  fontSize: 'body.medium',
                  fontWeight: 'body.medium',
                  lineHeight: 'body.medium',
                  letterSpacing: 'body.medium',
                })}
              >
                {translations.walletBelongsToMe}
              </span>

              <Controller
                control={control}
                defaultValue={defaultAddressInput?.userOwned}
                name="isMyWallet"
                render={({ field: { onChange, value } }) => {
                  return (
                    <CheckboxToggle
                      variant="primary"
                      size="small"
                      checked={value}
                      onChange={() => {
                        onChange(!value);

                        isMyWalletInputProps.onChange();
                      }}
                    />
                  );
                }}
              />
            </div>
          </div>
        )}

        <div className={inputSectionStyles}>
          <span
            className={`${inputTitleStyles} ${errors.walletAddress ? inputTitleErrorStyles : ''}`}
          >
            {translations.walletAddress}
          </span>
          <input
            aria-invalid={errors.walletAddress ? 'true' : 'false'}
            disabled={isWalletAddressDisabled}
            className={inputStyles}
            placeholder={translations.walletAddress}
            {...walletAddressInputProps}
          />
          {errors.walletAddress && (
            <div className={inputErrorContainerStyles}>
              <RegularDismissCircle size={12} color="negative.text_primary" />
              <span className={inputErrorTextStyles}>
                {errors.walletAddress.message as string}
              </span>
            </div>
          )}
        </div>

        {networkData?.destinationTag && (
          <div className={inputSectionStyles}>
            <span
              className={`${inputTitleStyles} ${errors.memo ? inputTitleErrorStyles : ''}`}
            >
              {translations.memoLabel}
            </span>
            <input
              className={inputStyles}
              placeholder={
                translations[networkData.destinationTag as DestinationTagValues]
              }
              {...memoInputProps}
            />
            {errors.memo && (
              <div className={inputErrorContainerStyles}>
                <RegularDismissCircle size={12} color="negative.text_primary" />
                <span className={inputErrorTextStyles}>
                  {errors.memo.message as string}
                </span>
              </div>
            )}
          </div>
        )}

        <div className={inputSectionStyles}>
          <span className={inputTitleStyles}>{translations.walletHost}</span>

          <Controller
            control={control}
            name="walletHost"
            defaultValue={defaultWalletHost}
            render={({ field: { value, onChange } }) => (
              <SelectWalletHostButton
                className={inputStyles}
                selectedWalletHost={value as CryptoTransfersVASPNode}
                translations={translations}
                walletHosts={walletHosts}
                onSelectWalletHost={(value) => {
                  onChange(value);

                  walletHostInputProps.onChange();
                }}
              />
            )}
          />
        </div>
        {walletHost?.id === OtherWalletId.SELF_HOSTED && (
          <div
            className={css({
              border: '2px solid',
              borderColor: 'information.stroke_primary',
              borderRadius: '8px',
              padding: 'small',
              display: 'flex',
              flexDirection: 'column',
              gap: 'extra_small_2',
            })}
          >
            <span
              className={css({
                color: 'information.text_primary',
                fontSize: 'label.medium_medium',
                fontWeight: 'label.medium_medium',
                letterSpacing: 'label.medium_medium',
                lineHeight: 'label.medium_medium',
              })}
            >
              {translations.selfHostedInfoTitle}
            </span>
            <span
              className={css({
                color: 'neutrals.text_primary',
                fontSize: 'body.medium',
                fontWeight: 'body.medium',
                letterSpacing: 'body.medium',
                lineHeight: 'body.medium',
              })}
            >
              {translations.selfHostedInfoSubtitle}
            </span>
            {/* enable when link is provided */}
            {/* <HyperLinkButtonBase
              noPadding
              size="small"
              visual="ghost"
              className={css({
                paddingTop: 'extra_small_2',
                textDecoration: 'underline',
                width: 'fit-content',
              })}
              to="/"
            >
              {translations.learnMore}
            </HyperLinkButtonBase> */}
          </div>
        )}

        {walletHost?.id === OtherWalletId.OTHER && (
          <div className={inputSectionStyles}>
            <span className={inputTitleStyles}>{translations.hostName}</span>
            <input
              className={inputStyles}
              placeholder={translations.hostName}
              {...otherHostNameInputProps}
            />
            <span
              className={css({
                color: 'neutrals.text_secondary',
                fontSize: 'caption.small_light',
                fontWeight: 'caption.small_light',
                lineHeight: 'caption.small_light',
                letterSpacing: 'caption.small_light',
              })}
            >
              {translations.hostNameSubtitle}
            </span>
          </div>
        )}
      </div>

      {children}

      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        })}
      >
        <span
          className={css({
            fontSize: 'body.medium',
            fontWeight: 'body.medium',
            lineHeight: 'body.medium',
            letterSpacing: 'body.medium',
          })}
        >
          {translations.walletCreationDisclaimer}
        </span>
        <Controller
          control={control}
          defaultValue={false}
          name="isAgreed"
          render={({ field: { onChange, value } }) => (
            <CheckboxToggle
              variant="primary"
              size="small"
              checked={value as boolean}
              onChange={onChange}
            />
          )}
        />
      </div>

      <div
        className={css({
          marginTop: 'auto',
          paddingY: 'extra_small',
        })}
      >
        <ButtonBase
          disabled={isSubmitDisabled || isSubmitting}
          isLoading={isSubmitting}
          visual="primary"
          size="large"
          type="submit"
          fullWidth
        >
          {submitButtonLabel}
        </ButtonBase>
      </div>
    </form>
  );
};

export { WalletForm };
